import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { EmptyState } from '../../../../components/EmptyState';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { Skeleton } from '../../../../components/Skeleton';
import { DateCell } from '../../../../components/table/DateCell';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { personDisplayName, usePersonNames } from '../../../human-resources/personnel/usePersonNames';
import { surveyQuestionnairesApi, surveyRespondentsApi } from '../surveyApi';
import {
  SURVEY_QUESTION_TYPE,
  SURVEY_QUESTION_TYPE_LABELS,
  type SurveyQuestionAnswerRow,
  type SurveyQuestionnaireQuestion,
  type SurveyQuestionTypeValue,
} from '../surveyTypes';
import { SurveyResponseViewModal } from './SurveyResponseViewModal';
import { useQuestionnaireOptions } from './useQuestionnaireOptions';

/** The type's name, under each question on the left — the form's own vocabulary. */
const TYPE_LABEL = new Map(SURVEY_QUESTION_TYPE_LABELS.map((t) => [t.value, t.label]));

/** How many answers a search reads — the persons resolve's own batch cap. */
const SEARCH_WINDOW = 100;

/** A machine value — a date, a time, a rating — is set in mono, as the answers window sets it. */
function isMachine(type: SurveyQuestionTypeValue): boolean {
  return type === SURVEY_QUESTION_TYPE.Date || type === SURVEY_QUESTION_TYPE.Time || type === SURVEY_QUESTION_TYPE.Scale;
}

/**
 * Responses › Question — TWO PANES, as Responses › Assessment (product call, 12 Oct 2026; it
 * replaced a Question dropdown over a half-width column): the questionnaire's questions on the
 * LEFT, and on the RIGHT the open question in the brand-topped header card with a card per
 * respondent carrying their answer.
 *
 * The survey's live responses, newest first — one page of a hundred. The rows carry each
 * respondent's `personId`, resolved to names in ONE batch; above the reader's access level a row
 * reads Restricted and its answer is withheld by the SERVER, not hidden here. The name opens that
 * respondent's whole response.
 */
export function SurveyQuestionView({ surveyId }: { surveyId: string }) {
  const questionnaires = useQuestionnaireOptions(surveyId);
  const [pickedQuestion, setPickedQuestion] = useState('');
  const [questions, setQuestions] = useState<SurveyQuestionnaireQuestion[]>([]);

  const questionnaire = questionnaires?.[0];
  const questionnaireId = questionnaire?.id ?? '';

  useEffect(() => {
    if (!questionnaireId) return;

    const controller = new AbortController();

    surveyQuestionnairesApi
      .get(surveyId, questionnaireId, controller.signal)
      .then((detail) => {
        if (!controller.signal.aborted) setQuestions(detail.questions);
      })
      .catch(() => {
        if (!controller.signal.aborted) setQuestions([]);
      });

    return () => controller.abort();
  }, [surveyId, questionnaireId]);

  // The first question of the questionnaire until the reader picks another.
  const questionId = (questions.some((q) => q.id === pickedQuestion) ? pickedQuestion : questions[0]?.id) ?? '';
  const index = questions.findIndex((q) => q.id === questionId);
  const question = index >= 0 ? questions[index] : undefined;

  if (questionnaires === null) return <Skeleton className="m-4 h-40" />;

  if (questionnaires.length === 0) {
    return <EmptyState message="This survey has no questionnaire yet. Create one under Configuration." />;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0">
      {/* LEFT — the questionnaire's questions, in its own order. Its own scroll region. */}
      <aside aria-label="Questions" className="flex w-80 shrink-0 flex-col border-r border-slate-200">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4">
          <div>
            <p className="truncate text-sm font-medium text-slate-800">{questionnaire?.title}</p>
            <p className="text-xs text-slate-500">
              <span className="font-mono tabular-nums">{questions.length}</span>{' '}
              {questions.length === 1 ? 'question' : 'questions'}
            </p>
          </div>
        </div>

        <ul className="modern-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {questions.length === 0 ? <li className="p-4 text-sm text-slate-500">This questionnaire has no questions.</li> : null}

          {questions.map((q, i) => {
            const open = q.id === questionId;

            return (
              <li key={q.id} className={`border-b border-slate-100 ${open ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                {/* The open question is the darkest text, on the tint — never a hue. */}
                <button
                  type="button"
                  id={`survey-question-open-${q.id}`}
                  aria-current={open ? 'true' : undefined}
                  onClick={() => setPickedQuestion(q.id)}
                  className="flex w-full gap-2 px-4 py-2.5 text-left"
                >
                  <span className="shrink-0 font-mono text-sm tabular-nums text-slate-500">{i + 1}.</span>
                  <span className="flex min-w-0 flex-col">
                    <span className={`line-clamp-2 text-sm ${open ? 'text-slate-900' : 'text-slate-600'}`}>
                      {q.text}
                      {q.isRequired ? <span className="ml-1 text-red-600">*</span> : null}
                    </span>
                    <span className="text-xs text-slate-500">{TYPE_LABEL.get(q.type)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* RIGHT — every respondent's answer to the open question, in the standard panel table. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {question ? (
          <QuestionAnswers
            key={question.id}
            surveyId={surveyId}
            questionnaireId={questionnaireId}
            question={question}
            number={index + 1}
          />
        ) : (
          <EmptyState message="This questionnaire has no questions." />
        )}
      </div>
    </div>
  );
}

/**
 * The open question's answers — the STANDARD panel table (product call, 13 Oct 2026; it was a
 * column of cards): server-paged, with the search strip, the record count, sorting on the date and
 * the pager. One row per live response; the question itself heads the table.
 *
 * The rows carry each respondent's `personId`, resolved to names in ONE batch. Above the reader's
 * access level a row reads Restricted and its answer is withheld by the SERVER, not hidden here.
 * The NAME is the row's anchor and opens that respondent's whole response.
 */
function QuestionAnswers({
  surveyId,
  questionnaireId,
  question,
  number,
}: {
  surveyId: string;
  questionnaireId: string;
  question: SurveyQuestionnaireQuestion;
  number: number;
}) {
  const canReadPeople = usePermission('personnel.read');
  const [viewing, setViewing] = useState<{ id: string; name: string } | null>(null);

  /*
    THE SEARCH REACHES THE EMPLOYEE'S NAME (product call, 13 Oct 2026) — and the server cannot:
    names live only in the PII store, with no index and no search of their own, by design. So a
    search is answered HERE, the way Responses › Assessment's list is: the question's answers are
    read whole (one page of a hundred, the resolve batch's own cap), the names resolved through the
    one endpoint that applies the access level, and the term matched against the name, the employee
    number and the answer. Without a search the server pages as before.

    Nothing is stored or sent anywhere new: the names are the ones this page would resolve anyway.
    The cost, said: past a hundred responses a search covers the newest hundred.
  */
  const fetchPage = useCallback(
    async (query: DataTableQuery, signal: AbortSignal) => {
      const term = query.search?.trim().toLowerCase() ?? '';

      if (term === '') {
        return surveyRespondentsApi.questionAnswers(surveyId, questionnaireId, question.id, query, signal);
      }

      const all = await surveyRespondentsApi.questionAnswers(
        surveyId,
        questionnaireId,
        question.id,
        { page: 1, pageSize: SEARCH_WINDOW, sort: query.sort },
        signal,
      );

      // `!= null`: a withheld id arrives as undefined. A reader without `personnel.read` resolves nothing.
      const ids = canReadPeople ? all.items.map((r) => r.personId).filter((id): id is string => id != null) : [];
      const resolved = ids.length > 0 ? await personsApi.resolve(ids, signal).catch(() => []) : [];
      const nameById = new Map(resolved.map((n) => [n.personId, n.displayName]));

      const matched = all.items.filter((row) => {
        // A restricted row matches on nothing: its name and its answer are both withheld.
        if (row.isRestricted) return false;

        const name = row.personId != null ? (nameById.get(row.personId) ?? '') : '';

        return [name, row.employeeNumber, row.answer ?? ''].some((value) => value.toLowerCase().includes(term));
      });

      const start = (query.page - 1) * query.pageSize;

      return { ...all, items: matched.slice(start, start + query.pageSize), total: matched.length, page: query.page, pageSize: query.pageSize };
    },
    [surveyId, questionnaireId, question.id, canReadPeople],
  );

  const state = useDataTable<SurveyQuestionAnswerRow>({ fetchPage, pageSize: 10, defaultSort: 'respondedAt:desc' });

  // `!= null`: a withheld id arrives as undefined, not null.
  const personIds = useMemo(
    () => (canReadPeople ? state.rows.map((r) => r.personId).filter((id): id is string => id != null) : []),
    [canReadPeople, state.rows],
  );

  const names = usePersonNames(personIds);

  const nameOf = useCallback(
    (row: SurveyQuestionAnswerRow) =>
      !canReadPeople && row.personId != null ? 'Hidden' : personDisplayName(row, names),
    [canReadPeople, names],
  );

  const columns = useMemo<DataColumn<SurveyQuestionAnswerRow>[]>(
    () => [
      // Resolved for this page from another database, so neither sortable nor searchable.
      { key: 'name', header: 'Name', width: 240, render: (row) => <TextCell value={nameOf(row)} /> },
      {
        key: 'answer',
        header: 'Answer',
        width: 420,
        summary: true,
        render: (row) =>
          row.isRestricted ? (
            // Withheld by the server above the reader's level — said, never left blank.
            <TextCell value="Withheld" />
          ) : question.type === SURVEY_QUESTION_TYPE.Name ? (
            // A Name answer is the respondent themselves.
            <TextCell value={nameOf(row)} />
          ) : (
            <TextCell
              value={
                row.answer == null
                  ? null
                  : question.type === SURVEY_QUESTION_TYPE.Date
                    ? formatDateOnly(row.answer)
                    : row.answer
              }
              mono={isMachine(question.type)}
            />
          ),
      },
      {
        key: 'respondedAt',
        header: 'Date Responded',
        width: 190,
        sortable: true,
        summary: true,
        render: (row) => <DateCell value={row.respondedAt} />,
      },
    ],
    [nameOf, question.type],
  );

  return (
    <div className="flex h-full min-w-0 flex-col gap-3 p-4">
      {/* The question the table answers — its number, text and note, as the left list names it. */}
      <div className="flex gap-2">
        <span className="shrink-0 font-mono text-sm tabular-nums text-slate-500">{number}.</span>
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="text-sm font-medium text-slate-800">
            {question.text}
            {question.isRequired ? <span className="ml-1 text-red-600">*</span> : null}
          </h2>
          {question.description ? (
            <p className="whitespace-pre-line text-xs text-slate-500">{question.description}</p>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <DataTablePanel<SurveyQuestionAnswerRow>
          id="survey-question-answers"
          state={state}
          columns={columns}
          rowKey={(row) => row.respondentId}
          searchPlaceholder="Search names and answers"
          fill
          // A read-only log: an answer is written only by answering, so nothing can be done to a row.
          selectable={false}
          // Date Responded IS the row's own instant; there is no creation metadata beside it.
          auditColumns={false}
          onRowOpen={(row) => setViewing({ id: row.respondentId, name: nameOf(row) })}
          openPermission="surveys.read"
          emptyMessage="No one has answered this questionnaire yet."
          emptySearchMessage="No name or answer matches that search."
        />
      </div>

      {viewing ? (
        <SurveyResponseViewModal
          open
          surveyId={surveyId}
          respondentId={viewing.id}
          respondentName={viewing.name}
          onClose={() => setViewing(null)}
        />
      ) : null}
    </div>
  );
}
